<template>
    <v-checkbox
        v-model="isChecked"
        class="mt-0 mb-2"
        :disabled="disabled"
        hide-details
        @change="onCheckboxChange"
    >
        <template #label>
            <div class="d-flex align-center">
                <span v-if="before_text">{{ before_text }}</span>
                <v-text-field
                    v-if="isChecked"
                    ref="inlineInput"
                    v-model.number="input_value"
                    type="number"
                    :min="min"
                    :max="max"
                    :step="step"
                    class="inline-text-input mx-2 flex-grow-0"
                    :style="{ width: input_width }"
                    :disabled="disabled"
                    dense
                    outlined
                    hide-details
                    @click.stop
                    @input="onInputChange"
                />
                <span v-else class="mx-2 text--disabled font-italic">{{ placeholder }}</span>
                <span v-if="after_text">{{ after_text }}</span>
            </div>
        </template>
    </v-checkbox>
</template>

<script>
/**
 * A checkbox with an inline number input field.
 * When the checkbox is checked, the input field is enabled and its value is emitted.
 * When the checkbox is unchecked, the input field is disabled and a predefined "unchecked" value is emitted.
 * In that unchecked state, the label shows a placeholder (___) instead of the input field.
 */

export default {
    name: 'CheckboxWithInlineInput',

    props: {
        /** The value that determines if checkbox is checked (e.g., > 0 means checked, null/0 means unchecked) */
        value: {
            type: Number,
            default: null,
        },
        /** Default value to use when checkbox is enabled */
        default_value: {
            type: Number,
            default: 7,
        },
        /** Text before the input field */
        before_text: {
            type: String,
            default: '',
        },
        /** Text after the input field */
        after_text: {
            type: String,
            default: '',
        },
        /** Placeholder text when checkbox is unchecked */
        placeholder: {
            type: String,
            default: '___',
        },
        /** Width of the input field */
        input_width: {
            type: String,
            default: '75px',
        },
        /** Minimum value for the number input */
        min: {
            type: [Number, String],
            default: 1,
        },
        /** Maximum value for the number input */
        max: {
            type: [Number, String],
            default: undefined,
        },
        /** Step for the number input */
        step: {
            type: [Number, String],
            default: 1,
        },
        /** Whether the input is disabled */
        disabled: {
            type: Boolean,
            default: false,
        },
        /** Value that represents "unchecked" state (typically 0) */
        unchecked_value: {
            type: Number,
            default: 0,
        },
    },

    data() {
        return {
            input_value: this.default_value,
        };
    },

    computed: {
        isChecked: {
            get() {
                return this.value != null && this.value > this.unchecked_value;
            },
            set(value) {
                // setter is needed for v-model, but actual logic is in the change handler
            }
        },
    },

    watch: {
        value: {
            immediate: true,
            handler(new_value) {
                // keep input_value in sync, but only if it's a valid value
                if (new_value != null && new_value > this.unchecked_value) {
                    this.input_value = new_value;
                }
            }
        }
    },

    methods: {
        onCheckboxChange(enabled) {
            if (enabled) {
                // when enabling, set to the stored input value (or default)
                this.$emit('input', this.input_value);

                this.$nextTick(() => {
                    if (this.$refs.inlineInput) {
                        this.$refs.inlineInput.$el.querySelector('input').focus();
                    }
                });
            } else {
                // when disabling, store current value and emit unchecked value
                if (this.value != null && this.value > this.unchecked_value) {
                    this.input_value = this.value;
                }
                this.$emit('input', this.unchecked_value);
            }
        },

        onInputChange() {
            if (this.input_value >= this.min) {
                this.$emit('input', this.input_value);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.inline-text-input {
    :deep(.v-input__slot) {
        padding-left: 0 !important;
        padding-right: 0 !important;
        min-height: 36px !important;
    }

    :deep(input) {
        padding-left: 1rem !important;
        padding-right: 0.75rem !important;
        padding-top: 0.25rem !important;
        padding-bottom: 0.25rem !important;
    }
}
</style>
